import React, { useContext, useEffect, useState } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { useChartTheme } from '../../themes/chartTheme';
import { Box, useTheme, Button } from '@mui/material';
import { tokens } from '../../theme';
import { FilterContext, IFilterState } from '../../contexts/FilterContext';
import fetchData from '../../helpers/fetchData';
import { dateToSqlDate } from '../../helpers/dateFuncs';
import { exportToExcel } from '../../helpers/exportFuncs';
import LoadingSpinner from '../LoadingSpinner';
import moment from 'moment';

interface IDoiTimelineData {
  date: string;
  soi_total: number;
  leads_total: number;
  doi_rate: number;
}

interface IStreamData {
  [key: string]: number | string;
}

const DoiTimeline: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const chartTheme = useChartTheme();
  const { filterState } = useContext(FilterContext)!;
  const [chartData, setChartData] = useState<IStreamData[]>([]);
  const [originalData, setOriginalData] = useState<IDoiTimelineData[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchDoiData = async (filterState: IFilterState): Promise<IDoiTimelineData[]> => {
    if (!filterState.startDate || !filterState.endDate || !filterState.network) {
      return [];
    }

    const networks = Array.isArray(filterState.network)
      ? filterState.network
      : [filterState.network];
    const themes = Array.isArray(filterState.theme)
      ? filterState.theme
      : filterState.theme
        ? [filterState.theme]
        : [];
    const params = {
      start_date: dateToSqlDate(filterState.startDate),
      end_date: dateToSqlDate(filterState.endDate),
      network: networks,
      ...(themes.length > 0 && { theme: themes }),
      ...(filterState.vnrVerlag.length > 0 && { verlag: filterState.vnrVerlag }),
    };

    return fetchData<IDoiTimelineData>('/api/vnr/doi-timeline', params) as Promise<
      IDoiTimelineData[]
    >;
  };

  const determineAggregation = (
    startDate: Date,
    endDate: Date,
    userSelection: string,
  ): 'daily' | 'weekly' | 'monthly' => {
    if (userSelection !== 'auto') {
      return userSelection as 'daily' | 'weekly' | 'monthly';
    }

    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays > 120) return 'monthly';
    if (diffDays > 45) return 'weekly';
    return 'daily';
  };

  const aggregateData = (
    data: IDoiTimelineData[],
    aggregationType: 'daily' | 'weekly' | 'monthly',
  ): IDoiTimelineData[] => {
    if (aggregationType === 'daily') return data;

    const aggregated: { [key: string]: IDoiTimelineData } = {};

    data.forEach((item) => {
      let key: string;
      const date = moment(item.date);

      if (aggregationType === 'weekly') {
        key = `${date.year()}-W${date.isoWeek().toString().padStart(2, '0')}`;
      } else {
        key = date.format('YYYY-MM');
      }

      if (!aggregated[key]) {
        aggregated[key] = {
          date: key,
          soi_total: 0,
          leads_total: 0,
          doi_rate: 0,
        };
      }

      aggregated[key].soi_total += parseFloat(String(item.soi_total)) || 0;
      aggregated[key].leads_total += parseInt(String(item.leads_total)) || 0;
    });

    // Calculate DOI rate for each aggregated period
    Object.values(aggregated).forEach((item) => {
      item.doi_rate = item.soi_total > 0 ? (item.leads_total / item.soi_total) * 100 : 0;
    });

    return Object.values(aggregated).sort((a, b) => a.date.localeCompare(b.date));
  };

  useEffect(() => {
    const loadData = async () => {
      const hasNetwork = Array.isArray(filterState.network)
        ? filterState.network.length > 0
        : !!filterState.network;

      if (!hasNetwork || !filterState.startDate || !filterState.endDate) {
        return;
      }

      setLoading(true);
      try {
        const rawData = await fetchDoiData(filterState);

        // Determine aggregation based on date range and user selection
        const aggregationType = determineAggregation(
          filterState.startDate,
          filterState.endDate,
          filterState.vnrAggregation,
        );
        const aggregatedData = aggregateData(rawData, aggregationType);

        // Prepare data for line chart
        const processedData = aggregatedData.map((item) => {
          const soiTotal = parseInt(String(item.soi_total)) || 0;
          const leadsTotal = parseInt(String(item.leads_total)) || 0;
          const doiRate = parseFloat(String(item.doi_rate)) || 0;

          return {
            date: item.date,
            SOIs: soiTotal,
            Leads: leadsTotal,
            'DOI Rate (%)': doiRate,
          };
        });

        setChartData(processedData);
        setOriginalData(aggregatedData);
      } catch (error) {
        console.error('Error fetching DOI timeline data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [
    filterState.network,
    filterState.theme,
    filterState.vnrVerlag,
    filterState.startDate,
    filterState.endDate,
    filterState.vnrAggregation,
  ]);

  const handleExport = () => {
    const exportData = chartData.map((item) => ({
      Date: item.date,
      SOIs: Number(item['SOIs']),
      Leads: Number(item['Leads']),
      'DOI Rate (%)': Number(item['DOI Rate (%)']).toFixed(2),
    }));
    exportToExcel(exportData, 'DOI_Timeline');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (chartData.length === 0) {
    return (
      <Box sx={{ margin: 2, textAlign: 'center', color: 'text.secondary' }}>
        <p>No DOI data available for the selected period and filters.</p>
      </Box>
    );
  }

  return (
    <Box sx={{ margin: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <h2 style={{ margin: 0 }}>VNR DOI Rate Timeline</h2>
        <Button variant="contained" color="primary" onClick={handleExport}>
          Export to Excel
        </Button>
      </Box>
      <Box sx={{ height: 400 }}>
        <ResponsiveLine
          data={(() => {
            // Find max for normalization
            const maxSoi = Math.max(...chartData.map((d) => Number(d.SOIs) || 0));
            const maxLeads = Math.max(...chartData.map((d) => Number(d.Leads) || 0));
            const maxCount = Math.max(maxSoi, maxLeads);
            const maxDoiRate = Math.max(...chartData.map((d) => Number(d['DOI Rate (%)']) || 0));

            return [
              {
                id: 'SOIs',
                yAxisId: 'left',
                data: chartData.map((item) => ({
                  x: item.date,
                  y: Number(item.SOIs),
                })),
              },
              {
                id: 'Leads',
                yAxisId: 'left',
                data: chartData.map((item) => ({
                  x: item.date,
                  y: Number(item.Leads),
                })),
              },
              {
                id: 'DOI Rate (%)',
                yAxisId: 'right',
                data: chartData.map((item) => {
                  const doiRate = Number(item['DOI Rate (%)']) || 0;
                  // Normalize DOI rate to fit roughly the same scale as counts
                  const normalizedRate = maxDoiRate > 0 ? (doiRate / maxDoiRate) * maxCount : 0;
                  return {
                    x: item.date,
                    y: normalizedRate,
                  };
                }),
              },
            ];
          })()}
          margin={{ top: 50, right: 110, bottom: 50, left: 60 }}
          xScale={{ type: 'point' }}
          yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false }}
          axisTop={null}
          axisRight={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'DOI Rate (%)',
            legendOffset: 40,
            legendPosition: 'middle',
            format: (value) => {
              if (chartData.length === 0) return value;
              // Convert normalized value back to actual DOI rate
              const maxCount = Math.max(
                ...chartData.map((d) => Math.max(Number(d.SOIs) || 0, Number(d.Leads) || 0)),
              );
              const maxDoiRate = Math.max(...chartData.map((d) => Number(d['DOI Rate (%)']) || 0));
              const actualRate = maxCount > 0 ? (value / maxCount) * maxDoiRate : 0;
              return actualRate.toFixed(1) + '%';
            },
          }}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Time Period',
            legendOffset: 36,
            legendPosition: 'middle',
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Count',
            legendOffset: -40,
            legendPosition: 'middle',
          }}
          colors={['#1f77b4', '#ff7f0e', '#2ca02c']}
          pointSize={8}
          pointColor={{ theme: 'background' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          lineWidth={3}
          theme={chartTheme}
          enableGridX={false}
          enableGridY={true}
          useMesh={true}
          tooltip={({ point }) => {
            const value = parseFloat(point.data.yFormatted as string);
            let displayValue;
            let unit;

            if (point.serieId === 'DOI Rate (%)') {
              // Convert normalized value back to actual DOI rate
              if (chartData.length === 0) {
                displayValue = value.toFixed(1);
                unit = '%';
              } else {
                const maxCount = Math.max(
                  ...chartData.map((d) => Math.max(Number(d.SOIs) || 0, Number(d.Leads) || 0)),
                );
                const maxDoiRate = Math.max(
                  ...chartData.map((d) => Number(d['DOI Rate (%)']) || 0),
                );
                const actualRate = maxCount > 0 ? (value / maxCount) * maxDoiRate : 0;
                displayValue = actualRate.toFixed(1);
                unit = '%';
              }
            } else {
              displayValue = value.toLocaleString('de-DE', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              });
              unit = '';
            }

            return (
              <Box
                sx={{
                  padding: '8px 12px',
                  background: colors.primary[400],
                  color: colors.grey[100],
                  borderRadius: 1,
                  boxShadow: 2,
                }}
              >
                <strong>{point.serieId}</strong>: {displayValue}
                {unit}
                <br />
                <small>{`Period: ${point.data.x}`}</small>
              </Box>
            );
          }}
          legends={[
            {
              anchor: 'bottom-right',
              direction: 'column',
              translateX: 100,
              itemWidth: 80,
              itemHeight: 20,
              itemTextColor: colors.grey[100],
              symbolSize: 12,
              symbolShape: 'circle',
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemTextColor: colors.grey[900],
                  },
                },
              ],
            },
          ]}
        />
      </Box>
    </Box>
  );
};

export default DoiTimeline;
